import React, { useEffect, useState } from"react";
import { useParams, useNavigate } from"react-router-dom";
import axios from"axios";
import API_BASE_URL from"../../Config/config";

const MockTestPage = ({ mode = "learner" }) => {
 const { topicTitle } = useParams();
 const navigate = useNavigate();

 const isTenantMode = mode === "tenant";

 const [questions, setQuestions] = useState([]);
 const [currentIndex, setCurrentIndex] = useState(0);
 const [answers, setAnswers] = useState({});
 const [elapsedSeconds, setElapsedSeconds] = useState(0);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState("");

 useEffect(() => {
 const timer = setInterval(() => setElapsedSeconds((prev) => prev + 1), 1000);
 return () => clearInterval(timer);
 }, []);

 const formatTime = (seconds) => {
 const mins = String(Math.floor(seconds / 60)).padStart(2,"0");
 const secs = String(seconds % 60).padStart(2,"0");
 return `00:${mins}:${secs}`;
 };

 useEffect(() => {
 const fetchQuestions = async () => {
 try {
 const decoded = decodeURIComponent(topicTitle);
 const res = await axios.get(`${API_BASE_URL}/api/mkgrid/topic/${decoded}`);
 setQuestions(res.data);
 } catch (err) {
 console.error("Error fetching questions:", err);
 setError("Failed to fetch questions.");
 } finally {
 setLoading(false);
 }
 };

 if (topicTitle) fetchQuestions();
 }, [topicTitle]);

 const handleOptionChange = (option) => {
 setAnswers((prev) => ({ ...prev, [currentIndex]: option }));
 };

 const handleQuestionChange = (index) => {
 setCurrentIndex(index);
 };

 const handleFinalSubmit = () => {
 const total = questions.length;
 let correct = 0;
 let wrong = 0;

 const answerMap = {};

 questions.forEach((q, idx) => {
 const userAnswer = answers[idx];
 const correctOption = q.correct_option;
 const title = q.title;

 if (userAnswer === correctOption) correct++;
 else wrong++;

 answerMap[title] = userAnswer || null;
 });

 const score = Math.round((correct / total) * 100);

 navigate(isTenantMode ? "/student/mock-result" : "/mockinterview/mock-result", {
 state: {
 topic: topicTitle,
 correct,
 wrong,
 score,
 time: formatTime(elapsedSeconds),
 total,
 user_id:"MockUser", // Replace with actual user logic if needed
 answers: answerMap,
 questions,
 },
 });
 };

 if (loading) return <div className="p-6 text-heading">Loading questions...</div>;
 if (error) return <div className="p-6 text-red-500">{error}</div>;
 if (questions.length === 0) return <div className="p-6 text-heading">No questions found.</div>;

 const currentQuestion = questions[currentIndex];
 const selectedOption = answers[currentIndex] ||"";

 return (
 <div className="flex bg-bgMain text-heading min-h-screen relative overflow-hidden font-sans">
 <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -mr-48 -mt-48"></div>
 
 {/* Sidebar */}
 <div className="w-72 bg-bgSection p-8 border-r border-accentGray/30 flex flex-col h-screen relative z-10 shadow-2xl">
 <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-primary opacity-50"></div>
 <h2 className="text-2xl font-black bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent uppercase tracking-tighter mb-10">
 TheSkillGenie
 </h2>
 
 <div className="space-y-1 mb-8">
 <p className="text-[10px] font-black text-primary uppercase tracking-widest">Topic</p>
 <p className="text-body font-extrabold leading-tight">{decodeURIComponent(topicTitle)}</p>
 </div>

 <div className="mb-10">
 <p className="text-[10px] font-black text-muted uppercase tracking-widest mb-3">Timer</p>
 <p className="text-4xl font-mono text-primary drop-shadow-[0_0_10px_rgba(34,211,238,0.3)]">{formatTime(elapsedSeconds)}</p>
 </div>

 <p className="text-[10px] font-black text-muted uppercase tracking-widest mb-4">Questions</p>
 <div className="grid grid-cols-5 gap-2 overflow-y-auto custom-scrollbar pr-2">
 {questions.map((_, idx) => (
 <button
 key={idx}
 onClick={() => handleQuestionChange(idx)}
 className={`w-10 h-10 rounded-xl text-xs font-black transition-all duration-200 ${
 currentIndex === idx
 ?"bg-primary text-white shadow-lg shadow-primary/30 scale-110"
 : answers[idx]
 ?"bg-primary/20 text-primary border border-primary/30"
 :"bg-bgMain text-muted border border-accentGray/30 hover:border-primary/50"
 }`}
 >
 {idx + 1}
 </button>
 ))}
 </div>
 </div>

 {/* Main Question Panel */}
 <div className="flex-1 p-8 md:p-12 overflow-y-auto relative z-10">
 <div className="flex justify-between items-center mb-10">
 <button
 onClick={() => navigate(-1)}
 className="flex items-center gap-2 text-muted hover:text-white transition-colors group"
 >
 <span className="w-8 h-8 rounded-full bg-bgSection flex items-center justify-center group-hover:bg-primary/20 transition-all">←</span>
 Quit Session
 </button>
 <div className="px-6 py-2 bg-primary/10 text-primary border border-primary/30 rounded-full text-[10px] font-black uppercase tracking-widest">
 Mock Interview • Phase 1
 </div>
 </div>

 <div className="max-w-4xl mx-auto">
 <div className="bg-bgSection p-8 md:p-12 rounded-[48px] shadow-2xl border border-accentGray/30 relative overflow-hidden">
 <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/10 rounded-full blur-3xl"></div>
 
 <div className="relative z-10">
 <div className="text-xs font-black text-primary mb-6 uppercase tracking-widest">
 Question {currentIndex + 1} of {questions.length}
 </div>
 <h2 className="text-2xl md:text-3xl font-extrabold text-heading mb-12 leading-relaxed tracking-tight">
          {currentQuestion.question_text}
        </h2>

 <div className="space-y-4 mb-12">
 {["A","B","C","D"].map((key) => {
 const isSelected = selectedOption === key;
 return (
 <label 
 key={key} 
 className={`flex items-center gap-5 p-6 rounded-3xl cursor-pointer transition-all duration-300 border-2 ${
 isSelected 
 ?"bg-primary/10 border-primary shadow-lg shadow-primary/10" 
 :"bg-bgSoft border-accentGray/30 hover:border-primary/30 hover:bg-bgMain"
 }`}
 >
 <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm transition-all ${
 isSelected ?"bg-primary text-white" :"bg-bgSection text-muted"
 }`}>
 {key}
 </div>
 <input
 type="radio"
 name={`question-${currentIndex}`}
 value={key}
 checked={isSelected}
 onChange={() => handleOptionChange(key)}
 className="hidden"
 />
 <span className={`text-lg ${isSelected ?"text-primary font-bold" :"text-body font-medium"}`}>
                  {currentQuestion[`option_${key.toLowerCase()}`]}
                </span>
 </label>
 );
 })}
 </div>

 <div className="flex justify-between items-center pt-10 border-t border-accentGray/30">
 <button
 onClick={() => handleQuestionChange(currentIndex - 1)}
 disabled={currentIndex === 0}
 className="text-muted font-black uppercase text-xs tracking-widest hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-all"
 >
 ← Previous
 </button>

 {currentIndex === questions.length - 1 ? (
 <button
 onClick={handleFinalSubmit}
 className="btn-primary-gradient px-12 py-4 rounded-2xl font-black text-lg shadow-xl hover:scale-105 active:scale-95 transition-all uppercase tracking-tighter"
 >
 Complete Test
 </button>
 ) : (
 <button
 onClick={() => handleQuestionChange(currentIndex + 1)}
 className={`px-10 py-4 rounded-2xl font-black transition-all shadow-xl uppercase tracking-widest ${
 answers[currentIndex]
 ?"bg-primary text-white hover:bg-primary/90 hover:scale-105"
 :"bg-bgMain text-muted border border-accentGray/30 opacity-50 cursor-not-allowed"
 }`}
 disabled={!answers[currentIndex]}
 >
 Next Question →
 </button>
 )}
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 );
};

export default MockTestPage;



